import { hasRule } from "./ruleSentence";
/**
 * WHICH ROWS CARRY A PROGRESSION RULE, AND WHICH SAY SO BY SAYING NOTHING.
 *
 * 🔴 F11 LESSON 27, ON THE COACH'S SIDE. The engine's own codex rule is that a reader with no
 * writer, or a writer with no reader, is a defect that fails SILENTLY — and `progression_config`
 * on `coach_exercises` is exactly that shape from the coach's chair. A row without one is not
 * broken: the workout still assigns, still logs, still finishes. It simply never progresses, and
 * nothing anywhere on the coach's screens says so.
 *
 * MEASURED LIVE, 2026-09-19 00:47 (`supabase db query --linked`, grouped by coach so the
 * denominator is one person's library and not two):
 *
 *     his library      142 rows —  111 carry NO progression rule,  31 do
 *     the other coach   42 rows —   42 carry NO progression rule,   0 do
 *
 * ⚠️ THE SPLIT IS THE MEASUREMENT. Ungrouped the table reads 184 rows / 153 without, which is a
 * number about nobody — the same denominator error that made "42 of 184 rows record nothing"
 * earlier the same night, where 42 was one coach's whole library and 0 of his 142 were affected.
 * A count shown to a coach is a count of THEIR rows.
 *
 * ⚠️ AND NOTHING HERE WRITES. This module reads a value the exercise editor already owns and
 * turns it into a sentence. It does not default a rule, does not suggest one, and does not touch
 * `progression_config` — a machine guessing a progression is how an athlete gets a number nobody
 * chose, and the column's own emptiness is the honest state until a coach fills it.
 */

/**
 * Does this stored `progression_config` amount to a rule?
 *
 * ⚠️ THREE SPELLINGS OF "NOTHING" AND ALL THREE ARE LIVE POSSIBILITIES on a jsonb column: SQL
 * NULL arrives as `null`, an empty object as `{}`, and a JSON null literal as the string
 * `"null"` when a writer stringifies before sending. Asking only `=== null` would count the
 * other two as rules and quietly report a library healthier than it is — the flattering
 * direction, which is the one that survives.
 *
 * An ARRAY is not a rule either: the column's shape is an object of named parameters, and `[]`
 * or `[…]` means something wrote the wrong shape. Treated as "no rule" rather than crashing,
 * because a coach seeing "no rule" investigates and a coach seeing a white screen does not.
 */
export function hasProgressionRule(config: unknown): boolean {
  // W73 (W72 step 5): ONE definition of "has a rule" for every web surface — `ruleSentence.hasRule`: the config must
  // NAME a rule this build applies. A non-empty object with no `rule` (or an unknown word) progresses nothing.
  return hasRule(config);
}

/** One workout row, as far as this question is concerned. */
export type RuleBearingRow = {
  /** The LIBRARY exercise this row prescribes — the rule lives there, never on the row. */
  exerciseId: string;
};

/**
 * How many of these rows prescribe an exercise with no progression rule.
 *
 * ⚠️ COUNTED IN ROWS, NOT IN EXERCISES. The same movement seated three times in a workout is
 * three rows that will not progress, and a coach fixing it fixes one library entry — but what
 * they are looking at on the screen is rows, and a count that says "4" beside a list of 21 rows
 * they can see is a count they can check. Deduping here would make the header disagree with the
 * badges directly underneath it.
 *
 * ⚠️ A ROW WHOSE EXERCISE IS NOT IN THE MAP IS NOT COUNTED AS MISSING. An id the library has not
 * returned means the library has not loaded, or the row points at something deleted — neither is
 * "this exercise has no progression rule", and reporting it as one would make every screen read
 * 21 of 21 for the second before the library arrives. `unknownRows` says how many there were, so
 * a caller can tell "nothing missing" from "nothing known".
 */
export function missingRuleCount(
  rows: readonly RuleBearingRow[],
  ruleByExerciseId: ReadonlyMap<string, boolean>,
): { missing: number; counted: number; unknownRows: number } {
  let missing = 0;
  let counted = 0;
  let unknownRows = 0;
  for (const row of rows) {
    const known = ruleByExerciseId.get(row.exerciseId);
    if (known === undefined) { unknownRows += 1; continue; }
    counted += 1;
    if (!known) missing += 1;
  }
  return { missing, counted, unknownRows };
}

/**
 * The sentence, or null when there is nothing to say.
 *
 * ⚠️ NULL WHEN NOTHING IS MISSING, rather than "0 of 21 rows carry no progression rule". A
 * zero-state banner is a line every coach reads once and then stops seeing, and it would sit on
 * every healthy workout for ever. Absence is the good news and it is said by silence.
 *
 * ⚠️ AND IT NEVER SAYS "of 0". A workout with no rows, or one whose library has not loaded, has
 * not been measured — it has not been found healthy.
 */
export function missingRuleSentence(
  count: { missing: number; counted: number },
): string | null {
  if (count.counted === 0) return null;
  if (count.missing === 0) return null;
  const rows = count.missing === 1 ? "row carries" : "rows carry";
  return `${count.missing} of ${count.counted} ${rows} no progression rule`;
}

/**
 * The badge a single row shows. Null when the row is fine or unknown — same reasoning as above,
 * one row at a time.
 */
export function rowRuleBadge(
  exerciseId: string,
  ruleByExerciseId: ReadonlyMap<string, boolean>,
): string | null {
  const known = ruleByExerciseId.get(exerciseId);
  if (known === undefined) return null;
  return known ? null : "no rule";
}

/**
 * Build the map every reader above takes, from whatever the library query returned.
 *
 * Kept here rather than at each call site so the three surfaces — the builder's rows, the
 * builder's header, the Workouts library row — cannot answer the same question differently.
 */
export function ruleMap(
  library: readonly { id: string; progressionConfig: unknown }[],
): Map<string, boolean> {
  const out = new Map<string, boolean>();
  for (const e of library) out.set(e.id, hasProgressionRule(e.progressionConfig));
  return out;
}
